"use client";

import React, { useState } from "react";
import { 
  X, 
  Ticket, 
  CheckCircle2, 
  AlertCircle, 
  ShieldCheck, 
  Flame, 
  Clock, 
  User, 
  Utensils, 
  RotateCcw,
  QrCode,
  ExternalLink
} from "lucide-react";

interface RestaurantVoucherRedeemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVoucherRedeemed?: () => void;
}

export default function RestaurantVoucherRedeemModal({
  isOpen,
  onClose,
  onVoucherRedeemed,
}: RestaurantVoucherRedeemModalProps) {
  const [voucherCode, setVoucherCode] = useState("");
  const [staffName, setStaffName] = useState("Rahul (Server)");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alreadyRedeemedInfo, setAlreadyRedeemedInfo] = useState<any | null>(null);
  const [successData, setSuccessData] = useState<any | null>(null);

  if (!isOpen) return null;

  const handleRedeem = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setAlreadyRedeemedInfo(null);
    setSuccessData(null);

    const clean = voucherCode.trim().toUpperCase();
    if (!clean) {
      setError("Please enter a voucher code.");
      return;
    }

    try {
      setLoading(true);
      const res = await fetch("/api/demos/restaurant/vouchers/redeem", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          voucher_code: clean,
          staff_name: staffName,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        if (data.alreadyRedeemed) {
          setAlreadyRedeemedInfo(data.voucher || { redeemed_at: "earlier", redeemed_by: "another server" });
        }
        throw new Error(data.error || "Failed to redeem voucher.");
      }

      setSuccessData(data);
      if (onVoucherRedeemed) {
        onVoucherRedeemed();
      }
    } catch (err: any) {
      setError(err.message || "Failed to verify voucher code.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setVoucherCode("");
    setError(null);
    setAlreadyRedeemedInfo(null);
    setSuccessData(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-md animate-in fade-in duration-200">
      <div className="relative w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl p-6 text-slate-100">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-800">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400">
              <Ticket className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white">Staff Voucher Redemption</h2>
              <p className="text-xs text-slate-400">
                Verify guest code & burn offer to stop double-dipping.
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="my-5">
          {!successData ? (
            <div className="space-y-4">
              {/* Floor Camera Scanner Option */}
              <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <QrCode className="w-4 h-4 text-amber-400" />
                  <div className="text-xs text-slate-300 font-medium">
                    Have waiter scan diner QR code?
                  </div>
                </div>
                <a
                  href="/demos/restaurant/redeem"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-2.5 py-1 bg-amber-500 hover:bg-amber-400 text-slate-950 text-[11px] font-bold rounded-lg transition flex items-center gap-1"
                >
                  <span>Open Scanner</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>

              <form onSubmit={handleRedeem} className="space-y-4">
              {/* Error Box */}
              {error && (
                <div className="p-3.5 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-xs">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold">{error}</div>
                      {alreadyRedeemedInfo && (
                        <div className="text-[11px] text-red-300/80 mt-1">
                          ⚠️ This voucher cannot be reused. Guest has already claimed this welcome offer.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Code Input */}
              <div>
                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">
                  Voucher Code (From Guest Phone)
                </label>
                <div className="relative">
                  <Ticket className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    required
                    value={voucherCode}
                    onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                    placeholder="e.g. SMOKE-APP-8192"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-9 pr-3 py-3 font-mono text-base font-bold tracking-wider text-amber-400 placeholder-slate-600 focus:outline-none focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/60"
                  />
                </div>

                {/* Quick test fillers */}
                <div className="flex items-center gap-2 mt-2 text-[11px] text-slate-400">
                  <span>Quick Test Codes:</span>
                  <button
                    type="button"
                    onClick={() => setVoucherCode("SMOKE-APP-4319")}
                    className="text-amber-400 underline hover:text-amber-300"
                  >
                    4319 (Active)
                  </button>
                  <span>•</span>
                  <button
                    type="button"
                    onClick={() => setVoucherCode("SMOKE-APP-8192")}
                    className="text-red-400 underline hover:text-red-300"
                  >
                    8192 (Redeemed)
                  </button>
                </div>
              </div>

              {/* Staff Member Tag */}
              <div>
                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">
                  Logged By (Staff / Cashier)
                </label>
                <div className="relative">
                  <User className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <select
                    value={staffName}
                    onChange={(e) => setStaffName(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-9 pr-3 py-2.5 text-xs text-slate-200 focus:outline-none focus:border-amber-500/60"
                  >
                    <option value="Rahul (Server)">Rahul (Server - Tables 1-6)</option>
                    <option value="Priya (Captain)">Priya (Captain - Floor Manager)</option>
                    <option value="Aarav (Head Sommelier)">Aarav (Head Sommelier)</option>
                    <option value="Main Cash Counter">Main Cash Counter</option>
                  </select>
                </div>
              </div>

              {/* Action Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold text-xs tracking-wider uppercase flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20 active:scale-[0.98] transition disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
                    <span>Verifying Code...</span>
                  </>
                ) : (
                  <>
                    <Flame className="w-4 h-4" />
                    <span>Burn & Apply Complimentary Item</span>
                  </>
                )}
              </button>
            </form>
          </div>
          ) : (
            /* Success confirmation */
            <div className="text-center py-2 animate-in fade-in zoom-in-95 duration-200">
              <div className="w-12 h-12 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 flex items-center justify-center mx-auto mb-3">
                <CheckCircle2 className="w-6 h-6" />
              </div>

              <h3 className="text-base font-bold text-white mb-1">
                Voucher Successfully Redeemed!
              </h3>
              <p className="text-xs text-slate-400 mb-4">
                1x Complimentary Chef's Bruschetta approved on guest's order.
              </p>

              {/* Receipt details */}
              <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 text-left text-xs space-y-2 mb-4">
                <div className="flex justify-between">
                  <span className="text-slate-500">Voucher Code:</span>
                  <span className="font-mono font-bold text-amber-400">{successData.voucher.voucher_code}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Guest Name:</span>
                  <span className="font-semibold text-white">{successData.voucher.guest_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Redeemed At:</span>
                  <span className="text-slate-300">{successData.voucher.redeemed_at}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Authorized Server:</span>
                  <span className="text-emerald-400 font-medium">{successData.voucher.redeemed_by}</span>
                </div>
              </div>

              {/* Customer WhatsApp confirmation notice */}
              <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs text-left mb-5 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>Automated WhatsApp confirmation receipt sent to customer's phone.</span>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleReset}
                  className="flex-1 py-2.5 px-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold flex items-center justify-center gap-1.5 transition"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>Redeem Another</span>
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 py-2.5 px-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-bold transition"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
